if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.hasAttribute('data-gallery-controller')) {
          this.initializeGallery();
          return;
        }
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initializeGallery() {
        this.track = this.elements.viewer?.querySelector('[id^="Slider-Gallery-"]');
        if (!this.track) return;
        this.galleryEvents = new AbortController();
        const options = { signal: this.galleryEvents.signal };
        this.previousArrow = this.elements.viewer.querySelector('button[name="previous"]');
        this.nextArrow = this.elements.viewer.querySelector('button[name="next"]');
        this.progress = this.querySelector('.ProductGallery__scrollbar-thumb');
        this.nativeGalleryProgress = !!window.CSS?.supports('animation-timeline: --nutracene-gallery-scroll') &&
          window.CSS.supports('timeline-scope: --nutracene-gallery-scroll');
        this.activeMedia = this.track.querySelector('.is-active') || this.track.querySelector('[data-media-id]');
        this.previousArrow?.addEventListener('click', () => this.navigateGallery(-1), options);
        this.nextArrow?.addEventListener('click', () => this.navigateGallery(1), options);
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
          thumbnail.querySelector('button')?.addEventListener('click', () => {
            this.setActiveMedia(thumbnail.dataset.target, false);
          }, options);
        });
        this.track.addEventListener('scroll', () => {
          if (!this.mql.matches && this.galleryScrollFrame == null) {
            this.galleryScrollFrame = requestAnimationFrame(() => {
              this.galleryScrollFrame = null;
              this.updateGalleryProgress();
            });
          }
          clearTimeout(this.galleryScrollTimer);
          this.galleryScrollTimer = setTimeout(() => this.syncGalleryFromScroll(), 150);
        }, { ...options, passive: true });
        this.track.addEventListener('pointerdown', () => {
          this.pendingMediaId = null;
          this.galleryRequest = (this.galleryRequest || 0) + 1;
        }, { ...options, passive: true });
        this.track.addEventListener('touchstart', () => {
          this.pendingMediaId = null;
          this.galleryRequest = (this.galleryRequest || 0) + 1;
        }, { ...options, passive: true });
        const recordGesture = (event) => {
          const point = event.touches?.[0] || event;
          this.galleryGesture = { x: point.clientX, y: point.clientY, left: this.track.scrollLeft };
        };
        this.track.addEventListener('pointerdown', recordGesture, { ...options, passive: true });
        this.track.addEventListener('touchstart', recordGesture, { ...options, passive: true });
        this.track.addEventListener('pointerup', (event) => this.finishGalleryGesture(event), options);
        this.track.addEventListener('touchend', (event) => this.finishGalleryGesture(event.changedTouches[0]), options);
        this.track.addEventListener('touchcancel', () => { this.galleryGesture = null; }, options);
        this.mql.addEventListener('change', () => this.realignGallery(), options);
        this.galleryTrackWidth = this.track.clientWidth;
        this.galleryResizeObserver = new ResizeObserver(() => {
          const width = this.track.clientWidth;
          if (width === this.galleryTrackWidth) return;
          this.galleryTrackWidth = width;
          this.realignGallery();
        });
        this.galleryResizeObserver.observe(this.track);
        this.refreshGalleryMetrics();
        this.updateGallerySelection(this.activeMedia);
        this.prepareAdjacentImages(this.activeMedia);

      }

      disconnectedCallback() {
        this.galleryRequest = (this.galleryRequest || 0) + 1;
        this.clearDesktopTransition();
        this.galleryEvents?.abort();
        this.galleryResizeObserver?.disconnect();
        clearTimeout(this.galleryScrollTimer);
        cancelAnimationFrame(this.galleryResizeFrame);
        cancelAnimationFrame(this.galleryScrollFrame);
        this.galleryScrollFrame = null;



      }

      connectedCallback() {
        if (this.hasAttribute('data-gallery-controller') && this.galleryEvents?.signal.aborted) {
          this.initializeGallery();
        }
      }

      galleryItems() {
        return Array.from(this.track.children).filter((item) => item.hasAttribute('data-media-id'));
      }

      refreshGalleryMetrics() {
        const items = this.galleryItems();
        const firstOffset = items[0]?.offsetLeft || 0;
        this.galleryMetrics = { items, offsets: items.map((item) => item.offsetLeft - firstOffset) };
        this.progress?.style.setProperty('--gallery-start-progress', String(1 / Math.max(1, items.length)));
      }

      updateGalleryProgress() {
        if (!this.progress) return;
        const metrics = this.galleryMetrics;
        if (!metrics?.items.length) return;
        if (!this.mql.matches && this.nativeGalleryProgress) return;
        const { items, offsets } = metrics;
        let position = Math.max(0, items.indexOf(this.activeMedia));
        if (!this.mql.matches) {
          let left = Math.max(0, Math.min(this.track.scrollLeft, offsets[offsets.length - 1]));
          const snappedOffset = offsets.find((offset) => Math.abs(offset - left) < 1);
          if (snappedOffset !== undefined) left = snappedOffset;
          position = 0;
          while (position < offsets.length - 1 && left >= offsets[position + 1]) position++;
          if (position < offsets.length - 1) {
            const distance = offsets[position + 1] - offsets[position];
            if (distance > 0) position += (left - offsets[position]) / distance;
          }
        }
        const fraction = Math.max(0, Math.min(1, (position + 1) / items.length));
        this.progress.style.width = '100%';
        this.progress.style.transform = `scaleX(${fraction})`;
      }

      updateGallerySelection(media) {
        if (!media) return;
        const items = this.galleryItems();
        items.forEach((item) => item.classList.toggle('is-active', item === media));
        this.activeMedia = media;

        const index = items.indexOf(media);
        this.previousArrow?.toggleAttribute('disabled', items.length <= 1);
        this.nextArrow?.toggleAttribute('disabled', items.length <= 1);
        this.updateGalleryProgress();
        if (this.elements.thumbnails) {
          this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${media.dataset.mediaId}"]`));
        }
      }

      finishGalleryGesture(point) {
        const gesture = this.galleryGesture;
        this.galleryGesture = null;
        if (!gesture || !point || this.mql.matches) return;
        const dx = point.clientX - gesture.x;
        const dy = point.clientY - gesture.y;
        if (Math.abs(dx) < 48 || Math.abs(dy) > Math.abs(dx) * .7) return;
        const last = this.galleryMetrics?.offsets.at(-1) || 0;
        if (last <= 0) return;
        if (dx < 0 && gesture.left >= last - 1 && this.track.scrollLeft >= last - 1) this.navigateGallery(1);
        if (dx > 0 && gesture.left <= 1 && this.track.scrollLeft <= 1) this.navigateGallery(-1);
      }

      navigateGallery(direction) {
        const items = this.galleryItems();
        const current = items.find((item) => item.dataset.mediaId === this.pendingMediaId) || this.activeMedia;
        if (items.length < 2) return;
        const nextIndex = (items.indexOf(current) + direction + items.length) % items.length;
        const next = items[nextIndex];
        if (next) this.selectGalleryMedia(next, false, direction);
      }

      syncGalleryFromScroll() {
        if (this.mql.matches) return;
        const items = this.galleryItems();
        if (!items.length) return;
        const firstOffset = items[0]?.offsetLeft || 0;
        const nearest = items.reduce((selected, item) => {
          const distance = Math.abs(item.offsetLeft - firstOffset - this.track.scrollLeft);
          const selectedDistance = Math.abs(selected.offsetLeft - firstOffset - this.track.scrollLeft);
          return distance < selectedDistance ? item : selected;
        }, items[0]);
        this.pendingMediaId = null;
        if (nearest !== this.activeMedia) this.updateGallerySelection(nearest);
        else this.updateGalleryProgress();
      }

      realignGallery() {
        this.clearDesktopTransition();
        cancelAnimationFrame(this.galleryResizeFrame);
        this.galleryResizeFrame = requestAnimationFrame(() => {
          if (!this.activeMedia) return;
          this.pendingMediaId = null;
          this.refreshGalleryMetrics();
          this.updateGallerySelection(this.activeMedia);
          if (!this.mql.matches) this.scrollGalleryTo(this.activeMedia, 'instant');
        });
      }

      scrollGalleryTo(media, behavior = 'smooth') {
        const firstOffset = this.galleryItems()[0]?.offsetLeft || 0;
        this.track.scrollTo({
          left: media.offsetLeft - firstOffset,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : behavior,
        });
      }

      clearDesktopTransition() {
        this.desktopAnimations?.forEach((animation) => animation.cancel());
        this.desktopAnimations = [];
        if (this.leavingMedia) {
          this.leavingMedia.classList.remove('is-gallery-leaving');
          this.leavingMedia.inert = this.leavingMediaWasInert;
          this.leavingMedia = null;
        }
      }

      animateDesktopTransition(previous, next, travelDirection) {
        this.clearDesktopTransition();
        if (!this.mql.matches || !previous || previous === next ||
            matchMedia('(prefers-reduced-motion: reduce)').matches || !next.animate) return;
        const items = this.galleryItems();
        const direction = travelDirection || (items.indexOf(next) > items.indexOf(previous) ? 1 : -1);
        this.leavingMedia = previous;
        this.leavingMediaWasInert = previous.inert;
        previous.inert = true;
        previous.classList.add('is-gallery-leaving');
        const timing = { duration: 300, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'both' };
        const outgoing = previous.animate([
          { transform: 'translateX(0)' },
          { transform: `translateX(${-direction * 100}%)` }
        ], timing);
        const incoming = next.animate([
          { transform: `translateX(${direction * 100}%)` },
          { transform: 'translateX(0)' }
        ], timing);
        this.desktopAnimations = [outgoing, incoming];
        this.desktopTransitionDone = Promise.allSettled(this.desktopAnimations.map((animation) => animation.finished));
        outgoing.onfinish = () => {
          if (this.leavingMedia === previous) this.clearDesktopTransition();
        };
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      prepareAdjacentImages(media) {
        const items = this.galleryItems();
        const index = items.indexOf(media);
        [...new Set([-1, 0, 1].map((step) => items[(index + step + items.length) % items.length]))].forEach((item) => {
          const image = item.querySelector('.product__media img');
          if (image) image.loading = 'eager';
        });
      }

      async selectGalleryMedia(media, prepend, travelDirection) {
        const request = this.galleryRequest = (this.galleryRequest || 0) + 1;
        this.pendingMediaId = media.dataset.mediaId;
        const image = media.querySelector('.product__media img');
        if (image) {
          image.loading = 'eager';
          if (!image.complete) {
            await new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true, signal: this.galleryEvents.signal });
              image.addEventListener('error', resolve, { once: true, signal: this.galleryEvents.signal });
            });
          }
        }

        if (request !== this.galleryRequest || !this.isConnected) return;
        const previous = this.activeMedia;
        this.clearDesktopTransition();
        if (prepend && media !== this.track.firstElementChild) {
          this.track.prepend(media);
          this.refreshGalleryMetrics();
        }
        this.updateGallerySelection(media);
        this.prepareAdjacentImages(media);
        this.animateDesktopTransition(previous, media, travelDirection);
        if (!this.mql.matches) {
          const items = this.galleryItems();
          const wraps = travelDirection && Math.abs(items.indexOf(media) - items.indexOf(previous)) === items.length - 1;
          this.scrollGalleryTo(media, wraps ? 'instant' : 'smooth');
        }
        this.preventStickyHeader();
        this.playActiveMedia(media);
        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${media.dataset.mediaId}"]`);
        if (thumbnail) this.announceLiveRegion(media, thumbnail.dataset.mediaPosition);
        if (this.mql.matches) this.pendingMediaId = null;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        if (this.track) {
          this.selectGalleryMedia(activeMedia, prepend);
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.track) {
            if (!this.mql.matches && this.pendingMediaId === activeMedia.dataset.mediaId) {
              this.scrollGalleryTo(activeMedia);
            }
            return;
          }
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

